import {useCallback, useEffect, useRef, useState} from 'react'

import {API_MODES, REFINE_ERROR_CODES} from '../lib/refineApi'
import {MAX_STORY_LENGTH} from '../lifeStoryTools'
import {REFINE_NOTES} from './useRefineStory'

export const INTERVIEW_PHASE = {IDLE: 'idle', LOADING: 'loading', ASK: 'ask', WRITING: 'writing', RESULT: 'result', ERROR: 'error', DONE: 'done'}
export const INTERVIEW_STEP = {QUESTIONS: 'questions', WRITE: 'write'}
export const ITEM_STATUS = {OPEN: 'open', ANSWERED: 'answered', SKIPPED: 'skipped'}

export const ENRICHED_LABEL = 'Enriched'
// Mirrors the API's cap: one to three good questions, four at most (TK-3204).
export const MAX_INTERVIEW_QUESTIONS = 4
// Read out when the last card is dismissed with nothing answered. Only a
// question with a quote has a highlight to reopen it from; an unanchored
// one floats below the story and, once closed, leaves Cancel as the way out.
export const ALL_QUESTIONS_CLOSED = 'All questions closed. Open a highlighted phrase to answer one, or cancel.'
export const ONLY_QUESTION_CLOSED = 'The question is closed. Open the highlighted phrase to answer it, or cancel.'
export const closedAnnouncement = items => {
  const one = items.length === 1

  if (!items.some(item => item.quote)) return `${one ? 'The question is' : 'All questions are'} closed. Cancel to go back to your story.`

  return one ? ONLY_QUESTION_CLOSED : ALL_QUESTIONS_CLOSED
}
// Mirrors the API's cap on one answer.
export const MAX_ANSWER_LENGTH = 500

export const INTERVIEW_NOTES = {
  alreadyCovers: 'Your story already covers the details your family would ask about.',
  noChanges: 'No changes made. Your story is just as you left it.',
  used: 'Your story is updated. Read it over and make sure it still sounds like you.',
  // The phone takeover's closing note once the woven story was taken.
  included: 'Your story now includes your answers.',
  questionsFailed: 'We couldn\u2019t look through your story just now. It\u2019s unchanged.',
  questionsFailedTakeover: 'We couldn\u2019t come up with questions just now. Your story is unchanged.',
  writeFailed: 'We couldn\u2019t finish writing just now. Your answers are still here.'
}

// Failures where trying the same request again can't help; these keep the
// refine notes' wording since the cause is the same.
const NO_RETRY_CODES = [REFINE_ERROR_CODES.RATE_LIMITED, REFINE_ERROR_CODES.UNAUTHORIZED, REFINE_ERROR_CODES.NO_ROOM]

const findQuote = (text, quote) => (quote ? text.toLowerCase().indexOf(quote.toLowerCase()) : -1)

/** Appends a chip to an answer: after a sentence end, or as the whole answer. */
export const appendExample = (answer, example) => {
  const current = (answer || '').trim()

  return current ? `${current.replace(/[.!?]?$/, '. ')}${example}` : example
}

/**
 * The questions the model returned, ready to ask. A quote points at the story
 * only when those words really are there (the view highlights them), and
 * two questions can't share one; anything else is asked on its own.
 */
export const toItems = (questions, text) => {
  const seen = new Set()

  return (questions || []).slice(0, MAX_INTERVIEW_QUESTIONS).map(question => {
    const key = (question.quote || '').toLowerCase()
    const anchored = key && findQuote(text, question.quote) >= 0 && !seen.has(key)

    if (anchored) seen.add(key)

    return {
      text: question.text,
      quote: anchored ? question.quote : '',
      examples: question.examples || [],
      answer: '',
      status: ITEM_STATUS.OPEN
    }
  })
}

const answered = items => (items || []).filter(item => item.answer.trim()).length
const lastAnswered = items => {
  for (let i = (items || []).length - 1; i >= 0; i--) if (items[i].answer.trim()) return i

  return -1
}

/**
 * The Enrich interview (TK-3204, prototype `startFamily` … `famUse`): the
 * model reads the story and asks a few follow-up questions; the writer
 * answers or skips each; one finishing rewrite weaves the answers in; the
 * writer uses that version or keeps the original.
 *
 * The hook owns the interview only. The story text is read once through
 * `getText` when the interview starts (`base`) and never touched until
 * `onUse({text, base, answeredCount})`, which the screen turns into a
 * refine-style apply with an undo entry. Notes for the prompt's note strip
 * go through `showNote`; announcements through `announce`.
 *
 * Failures land in the ERROR phase with `error.step` so the desktop view
 * can turn them into a note (and drop back to asking after a failed write)
 * while the phone takeover shows its error screen.
 *
 * The takeover has two more stops the desktop card does not: `accept` takes
 * the woven story but stays open, and `finish` moves to the DONE screen, from
 * which `cancel` closes with the note that fits (`accepted` says which).
 */
const useEnrichInterview = ({
  getText, question, chapter, chapters, request, onUse, onLastDismissed = () => {}, showNote = () => {}, announce = () => {}, logEvent = () => {}
}) => {
  const [phase, setPhase] = useState(INTERVIEW_PHASE.IDLE)
  const [items, setItems] = useState(null)
  const [active, setActive] = useState(-1)
  const [woven, setWoven] = useState('')
  const [marks, setMarks] = useState([])
  const [error, setError] = useState(null)
  const [accepted, setAccepted] = useState(false)
  // A write refused for a reason trying again can't fix (no room in the
  // story): the answers stay, but Write my story waits for one to change.
  const [writeRefused, setWriteRefused] = useState(false)
  const writeRefusedRef = useRef(false)

  const baseRef = useRef('')
  const controllerRef = useRef(null)
  // Bumped on every start/end so a reply from an earlier interview is ignored.
  const sessionRef = useRef(0)
  const itemsRef = useRef(items)
  const activeRef = useRef(active)
  const phaseRef = useRef(phase)
  const optionsRef = useRef({getText, question, chapter, chapters, request, onUse, onLastDismissed, showNote, announce, logEvent})

  itemsRef.current = items
  activeRef.current = active
  phaseRef.current = phase
  writeRefusedRef.current = writeRefused
  optionsRef.current = {getText, question, chapter, chapters, request, onUse, onLastDismissed, showNote, announce, logEvent}

  const abort = useCallback(() => {
    if (controllerRef.current) {
      controllerRef.current.abort()
      controllerRef.current = null
    }
  }, [])

  const send = useCallback(attributes => {
    abort()

    const controller = typeof AbortController === 'function' ? new AbortController() : {abort: () => {}, signal: undefined}

    controllerRef.current = controller

    const {question: prompt, chapter: chapterName, chapters: chapterNames, request: call} = optionsRef.current
    const session = sessionRef.current
    const promise = call({question: prompt, chapter: chapterName, chapters: chapterNames, ...attributes}, controller.signal)

    return promise.then(
      result => {
        if (sessionRef.current !== session || controllerRef.current !== controller) return null

        controllerRef.current = null

        return result
      },
      failure => {
        if (sessionRef.current !== session || controllerRef.current !== controller) return null

        controllerRef.current = null
        throw failure
      }
    )
  }, [abort])

  const end = useCallback(() => {
    abort()
    sessionRef.current += 1
    setPhase(INTERVIEW_PHASE.IDLE)
    setItems(null)
    setActive(-1)
    setWoven('')
    setMarks([])
    setError(null)
    setAccepted(false)
    setWriteRefused(false)
  }, [abort])

  const fail = useCallback((step, failure) => {
    const code = (failure && failure.code) || REFINE_ERROR_CODES.UPSTREAM_ERROR

    if (code === REFINE_ERROR_CODES.CANCELLED) return

    const retry = !NO_RETRY_CODES.includes(code)

    setError({step, code, retry})
    if (step === INTERVIEW_STEP.WRITE && !retry) setWriteRefused(true)
    setPhase(INTERVIEW_PHASE.ERROR)
    setActive(-1)
    optionsRef.current.announce(step === INTERVIEW_STEP.QUESTIONS ? 'We couldn\u2019t look through your story. It\u2019s unchanged.' : 'We couldn\u2019t finish writing. Your answers are still here.')
    optionsRef.current.logEvent('failed_life_story_refine', {
      wildcard: step === INTERVIEW_STEP.QUESTIONS ? API_MODES.QUESTIONS : API_MODES.WEAVE,
      scope: 'story',
      failure_reason: code
    })
  }, [])

  /**
   * Asks for the questions about the story as it was read at start
   * (`baseRef`); Try again after a failed ask comes back here, not through
   * `start`, so edits made while the failure was showing do not change the
   * story the interview is about.
   */
  const askQuestions = useCallback(() => {
    const {announce: say, showNote: note} = optionsRef.current

    setError(null)
    setAccepted(false)
    setWriteRefused(false)
    setPhase(INTERVIEW_PHASE.LOADING)
    say('Reading your story the way your family would.')

    return send({mode: API_MODES.QUESTIONS, text: baseRef.current}).then(
      result => {
        if (!result) return

        const nextItems = toItems(result.interviewQuestions, baseRef.current)

        if (!nextItems.length) {
          end()
          note({kind: 'info', code: 'already_covers', message: INTERVIEW_NOTES.alreadyCovers})

          return
        }

        setItems(nextItems)
        setActive(0)
        setPhase(INTERVIEW_PHASE.ASK)
        say(`${nextItems.length === 1 ? 'One question' : `${nextItems.length} questions`}. ${nextItems[0].text}`)
      },
      failure => fail(INTERVIEW_STEP.QUESTIONS, failure)
    )
  }, [send, end, fail])

  /** Reads the story once and asks for the questions. */
  const start = useCallback(() => {
    sessionRef.current += 1
    baseRef.current = optionsRef.current.getText()
    setItems(null)
    setActive(-1)
    setWoven('')
    setMarks([])

    return askQuestions()
  }, [askQuestions])

  /** Opens question `k` (or closes it when it is already open). */
  const open = useCallback(k => {
    if (phaseRef.current !== INTERVIEW_PHASE.ASK || !itemsRef.current || !itemsRef.current[k]) return

    const next = activeRef.current === k ? -1 : k

    setActive(next)

    if (next > -1) optionsRef.current.announce(itemsRef.current[k].text)
  }, [])

  /** Previous (-1) / Next (+1) question, without changing its status. */
  const jump = useCallback(direction => {
    const list = itemsRef.current

    if (phaseRef.current !== INTERVIEW_PHASE.ASK || !list) return

    const next = activeRef.current + direction

    if (next < 0 || next >= list.length) return

    setActive(next)
    optionsRef.current.announce(`Question ${next + 1} of ${list.length}. ${list[next].text}`)
  }, [])

  // A refused write is held until an answer changes; the change lifts the
  // hold and takes the failure note with it, so Write my story never comes
  // back enabled under a stale "no room" message.
  const liftWriteHold = () => {
    if (!writeRefusedRef.current) return

    setWriteRefused(false)
    optionsRef.current.showNote(null)
  }

  const setAnswer = useCallback(value => {
    const k = activeRef.current

    if (k < 0) return

    liftWriteHold()
    setItems(list => list && list.map((item, i) => (i === k ? {...item, answer: value.slice(0, MAX_ANSWER_LENGTH)} : item)))
  }, [])

  const addExample = useCallback(example => {
    const k = activeRef.current

    if (k < 0) return

    liftWriteHold()
    setItems(list => list && list.map((item, i) => (i === k ? {...item, answer: appendExample(item.answer, example).slice(0, MAX_ANSWER_LENGTH)} : item)))
  }, [])

  /** Writes the answers into the story with one finishing rewrite. */
  const write = useCallback(() => {
    const list = itemsRef.current

    if (!list || !answered(list) || writeRefusedRef.current) return Promise.resolve()

    const {announce: say, showNote: note} = optionsRef.current
    const answers = list
      .filter(item => item.answer.trim())
      .map(item => ({question: item.text, quote: item.quote, answer: item.answer.trim()}))

    setActive(-1)
    setError(null)
    // A failed write's note (and its Try again) described the last attempt;
    // it goes whichever button starts this one, not only the note's own.
    note(null)
    setPhase(INTERVIEW_PHASE.WRITING)
    say('Writing your story with everything you shared.')

    return send({mode: API_MODES.WEAVE, text: baseRef.current, answers}).then(
      result => {
        if (!result) return

        // The API keeps the woven story under the cap; the check is
        // belt-and-braces so nothing over it can ever be applied and saved.
        // It reads as a write failure with no retry (the answers are kept).
        if ((result.refinedText || '').length > MAX_STORY_LENGTH) {
          fail(INTERVIEW_STEP.WRITE, {code: REFINE_ERROR_CODES.NO_ROOM})

          return
        }

        setWoven(result.refinedText)
        setMarks(result.marks || [])
        setPhase(INTERVIEW_PHASE.RESULT)
        say('Your story is ready. New details are highlighted.')
      },
      failure => fail(INTERVIEW_STEP.WRITE, failure)
    )
  }, [send, fail])

  /**
   * The card's one button: Next / Skip moves on, marking the question
   * answered or skipped; on the last question, Write my story when anything
   * was answered, otherwise Done closes the card.
   */
  const go = useCallback(() => {
    const list = itemsRef.current
    const k = activeRef.current

    if (phaseRef.current !== INTERVIEW_PHASE.ASK || !list || !list[k]) return

    const has = Boolean(list[k].answer.trim())
    const nextItems = list.map((item, i) => (i === k ? {...item, status: has ? ITEM_STATUS.ANSWERED : ITEM_STATUS.SKIPPED} : item))

    itemsRef.current = nextItems
    setItems(nextItems)

    if (k + 1 < nextItems.length) {
      setActive(k + 1)
      optionsRef.current.announce(`Next question. ${nextItems[k + 1].text}`)

      return
    }

    if (answered(nextItems)) {
      write()

      return
    }

    // Done on the last question with nothing answered: the card closes and
    // the screen decides where focus goes (the footer's Cancel).
    setActive(-1)
    optionsRef.current.announce(closedAnnouncement(nextItems))
    optionsRef.current.onLastDismissed()
  }, [write])

  const handOver = useCallback(keepOpen => {
    const {onUse: apply} = optionsRef.current

    apply({text: woven, base: baseRef.current, answeredCount: answered(itemsRef.current), keepOpen})
  }, [woven])

  /** Use this version: hands the woven story to the screen and ends. */
  const use = useCallback(() => {
    if (phaseRef.current !== INTERVIEW_PHASE.RESULT) return

    end()
    handOver(false)
  }, [end, handOver])

  /** Use this version in the takeover: hands the story over and goes on to the DONE screen. */
  const accept = useCallback(() => {
    if (phaseRef.current !== INTERVIEW_PHASE.RESULT) return

    setAccepted(true)
    setActive(-1)
    setPhase(INTERVIEW_PHASE.DONE)
    handOver(true)
  }, [handOver])

  /** Keep my original / Done with nothing answered, in the takeover: the DONE screen, nothing changed. */
  const finish = useCallback(() => {
    if (phaseRef.current === INTERVIEW_PHASE.IDLE) return

    abort()
    setActive(-1)
    setPhase(INTERVIEW_PHASE.DONE)
  }, [abort])

  /**
   * Cancel, Keep my original, Escape, Back to my story: closes the interview
   * with a note saying what happened, unless `silent`. The takeover adds
   * what became of its recording as `addendum`.
   */
  const cancel = useCallback(({silent = false, addendum = null} = {}) => {
    if (phaseRef.current === INTERVIEW_PHASE.IDLE) return

    const wasAccepted = accepted

    end()

    if (silent) return

    const bits = [wasAccepted && INTERVIEW_NOTES.included, addendum].filter(Boolean)

    optionsRef.current.showNote(
      bits.length ?
        {kind: 'info', code: wasAccepted ? 'included' : 'recorded', message: bits.join(' ')} :
        {kind: 'info', code: 'no_changes', message: INTERVIEW_NOTES.noChanges}
    )
  }, [end, accepted])

  /**
   * Back to the questions with the answers kept. From the result the last
   * question opens again, as it does when `open` asks for it (the takeover
   * always has one question up). From a failed write the last answered
   * question reopens: its answer is the one to change when the write was
   * refused for lack of room, and a free question (no highlight to click)
   * has no other way back to it.
   */
  const backToQuestions = useCallback(({open = false} = {}) => {
    const from = phaseRef.current
    const list = itemsRef.current

    if ((from !== INTERVIEW_PHASE.ERROR && from !== INTERVIEW_PHASE.RESULT) || !list) return

    setError(null)
    setActive(from === INTERVIEW_PHASE.RESULT || open ? list.length - 1 : lastAnswered(list))
    setPhase(INTERVIEW_PHASE.ASK)
  }, [])

  /** Try again after a failure: the questions again, or the write again. */
  const tryAgain = useCallback(() => {
    const failed = error

    if (!failed) return Promise.resolve()

    return failed.step === INTERVIEW_STEP.WRITE && itemsRef.current ? write() : askQuestions()
  }, [error, askQuestions, write])

  // Leaving the screen mid-interview drops the request; nothing was applied.
  useEffect(() => () => abort(), [abort])

  const list = items || []
  const activeItem = active > -1 ? list[active] : null
  const answeredCount = answered(list)
  const canWrite = answeredCount > 0 && !writeRefused
  const isLast = active === list.length - 1
  const hasAnswer = Boolean(activeItem && activeItem.answer.trim())

  let goLabel = null

  if (activeItem) {
    if (!isLast) goLabel = hasAnswer ? 'Next' : 'Skip'
    else goLabel = hasAnswer || answeredCount ? 'Write my story' : 'Done'
  }

  return {
    phase,
    items,
    active,
    activeItem,
    answeredCount,
    canWrite,
    goLabel,
    // The card's forward button is off only when it would write and can't.
    goDisabled: goLabel === 'Write my story' && !canWrite,
    // Primary styling for the button that moves the interview forward.
    goPrimary: Boolean(activeItem) && (isLast ? hasAnswer || answeredCount > 0 : hasAnswer),
    base: baseRef.current,
    woven,
    marks,
    error,
    accepted,
    errorMessage: error ?
      (!error.retry && REFINE_NOTES.errors[error.code]) ||
        (error.step === INTERVIEW_STEP.QUESTIONS ? INTERVIEW_NOTES.questionsFailed : INTERVIEW_NOTES.writeFailed) :
      null,
    running: phase !== INTERVIEW_PHASE.IDLE,
    start,
    open,
    jump,
    setAnswer,
    addExample,
    go,
    write,
    use,
    accept,
    finish,
    cancel,
    backToQuestions,
    tryAgain
  }
}

export default useEnrichInterview
